import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEmpty, isEqual } from 'lodash';

import {
  BOUserTypeEnum,
  FullBOUserResponseModel,
  AccountDocumentTypeEnum,
  MemberHistoryDetailsModel,
  BusinessInfoResponseModel,
} from 'api/adminApiClient';
import HistoryLog from '../../HistoryLog';
import HistoryField from '../../HistoryField';
import BaseAlert from 'components/wrappers/BaseAlert';
import { einMask, ssnMask } from 'utils/validationUtils';
import { getMaskedNumberValue } from 'utils/numberUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getEinTypeLabel, isEinOption } from 'utils/entityUtils';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import { getRecruiterHistoryFile } from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../../HistoryCard.module.less';

type Props = {
  userId?: string;
  historyDetails: MemberHistoryDetailsModel;
  historyId: string;
};

export default function EntityInformationDetailsCard(props: Props) {
  const { historyDetails, userId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const hasEntityFileKeyChanged = !isEqual(
    oldData?.businessInfo?.entityInformation?.entityInfoFileKey || '',
    newData?.businessInfo?.entityInformation?.entityInfoFileKey || '',
  );

  const handleDownloadW8W9 = async (fileKey?: string) => {
    const result = await dispatch(
      getRecruiterHistoryFile({
        userId,
        fileKey,
        documentType: AccountDocumentTypeEnum.EntityInfo,
      }),
    );

    if (getRecruiterHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderCompanyEntityInformation = (
    businessInfo?: BusinessInfoResponseModel,
  ) => {
    const hasLegalCompanyNameChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.name || '',
      newData?.businessInfo?.entityInformation?.name || '',
    );
    const hasEinChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.number || '',
      newData?.businessInfo?.entityInformation?.number || '',
    );
    const hasCompanyEntityInfoChanged =
      hasLegalCompanyNameChanged || hasEinChanged;

    if (!hasCompanyEntityInfoChanged) {
      return null;
    }
    const sectionName = 'entity-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Entity information</div>
          </Col>

          {hasLegalCompanyNameChanged && (
            <HistoryField
              title="Legal company name"
              value={businessInfo?.entityInformation?.name}
              dataId={`${sectionName}-company-name-field`}
            />
          )}

          {hasEinChanged && (
            <HistoryField
              title="EIN"
              value={getMaskedNumberValue(
                businessInfo?.entityInformation?.number,
                einMask,
              )}
              dataId={`${sectionName}-EIN-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderSelfEmployedEntityInformation = (
    businessInfo?: BusinessInfoResponseModel,
  ) => {
    const hasLegalNameChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.name || '',
      newData?.businessInfo?.entityInformation?.name || '',
    );
    const hasTaxIdChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.numberType || '',
      newData?.businessInfo?.entityInformation?.numberType || '',
    );
    const hasEinChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.number || '',
      newData?.businessInfo?.entityInformation?.number || '',
    );
    const hasSelfEmployedEntityInfoChanged =
      hasLegalNameChanged || hasTaxIdChanged || hasEinChanged;

    if (!hasSelfEmployedEntityInfoChanged) {
      return null;
    }

    const isOldEntityInfoEmpty = Object.values(
      historyDetails.oldData?.businessInfo?.entityInformation || {},
    ).every(isEmpty);

    const taxIdMask = isEinOption(businessInfo?.entityInformation?.numberType)
      ? einMask
      : ssnMask;
    const sectionName = 'entity-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Entity information</div>
          </Col>

          {hasLegalNameChanged && (
            <HistoryField
              title="Legal name"
              value={businessInfo?.entityInformation?.name}
              dataId={`${sectionName}-legal-name-field`}
            />
          )}

          {hasTaxIdChanged && (
            <HistoryField
              title="Tax ID"
              value={
                businessInfo?.entityInformation?.numberType &&
                getEinTypeLabel(businessInfo?.entityInformation?.numberType)
              }
              dataId={`${sectionName}-taxId-field`}
            />
          )}

          {(hasEinChanged || hasTaxIdChanged) && (
            <HistoryField
              title={getEinTypeLabel(
                // For the first edit numberType is empty in the old snapshot
                // So numberType is taken from the new one
                isOldEntityInfoEmpty
                  ? newData?.businessInfo?.entityInformation?.numberType
                  : businessInfo?.entityInformation?.numberType,
              )}
              value={getMaskedNumberValue(
                businessInfo?.entityInformation?.number,
                taxIdMask,
              )}
              dataId={`${sectionName}-${getEinTypeLabel(
                isOldEntityInfoEmpty
                  ? newData?.businessInfo?.entityInformation?.numberType
                  : businessInfo?.entityInformation?.numberType,
              )}-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderInternationalEntityInformation = (
    businessInfo?: BusinessInfoResponseModel,
  ) => {
    const hasLegalCompanyNameChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.name || '',
      newData?.businessInfo?.entityInformation?.name || '',
    );
    const hasForeignTinChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.number || '',
      newData?.businessInfo?.entityInformation?.number || '',
    );
    const hasCountryChanged = !isEqual(
      oldData?.businessInfo?.entityInformation?.countryName || '',
      newData?.businessInfo?.entityInformation?.countryName || '',
    );
    const hasInternationalEntityInfoChanged =
      hasLegalCompanyNameChanged || hasForeignTinChanged || hasCountryChanged;

    if (!hasInternationalEntityInfoChanged) {
      return null;
    }
    const sectionName = 'entity-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Entity information</div>
          </Col>

          {hasLegalCompanyNameChanged && (
            <HistoryField
              title="Legal company name"
              value={businessInfo?.entityInformation?.name}
              dataId={`${sectionName}-legal-name-field`}
            />
          )}

          {hasForeignTinChanged && (
            <HistoryField
              title="Foreign TIN"
              value={businessInfo?.entityInformation?.number}
              dataId={`${sectionName}-foreign-TIN-field`}
            />
          )}

          {hasCountryChanged && (
            <HistoryField
              title="Country of incorporation"
              value={businessInfo?.entityInformation?.countryName}
              dataId={`${sectionName}-country-name-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderEntityInformation = (
    recruiterProfile?: FullBOUserResponseModel,
  ) => {
    if (!recruiterProfile) return null;

    const { boUserType, businessInfo } = recruiterProfile;

    return (
      <>
        {boUserType === BOUserTypeEnum.Company && (
          <>{renderCompanyEntityInformation(businessInfo)}</>
        )}

        {boUserType === BOUserTypeEnum.SelfEmployed && (
          <>{renderSelfEmployedEntityInformation(businessInfo)}</>
        )}

        {boUserType === BOUserTypeEnum.International && (
          <>{renderInternationalEntityInformation(businessInfo)}</>
        )}

        {hasEntityFileKeyChanged && (
          <>
            <Row gutter={[10, 10]}>
              <Col xs={24}>
                <div className={styles.cardTitle}>
                  {boUserType === BOUserTypeEnum.International ? 'W8' : 'W9'}
                </div>
              </Col>
              <HistoryField
                emptyText="No file"
                value={
                  businessInfo?.entityInformation?.entityInfoFileName && (
                    <span
                      className="link-imitation"
                      onClick={() =>
                        handleDownloadW8W9(
                          businessInfo?.entityInformation?.entityInfoFileKey,
                        )
                      }
                    >
                      {getFileNameWithoutExt(
                        businessInfo.entityInformation.entityInfoFileName,
                      )}
                    </span>
                  )
                }
                dataId="entity-information-file-field"
              />
            </Row>

            <Divider />
          </>
        )}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderEntityInformation(oldData)}
      newData={renderEntityInformation(newData)}
    />
  );
}
